import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { MultiLineChartHook } from '../../templates/charts/hooks/MultiLineChartHook'
import * as cf from "../../templates/charts/ChartConstants"
import * as con from "../../GlobalConstants"
import { round } from '../../GlobalFunctions'
import { addDays, addMonths, daysBetweenDates, formatDateForUser } from '../../utils/dateFunctions'
import { getGeneratorScenarioFunctionByVolatility } from '../../utils/scenarioFunctions'
import { RefMarketValueHook, RefSPOT } from '../hooks/MarketValuesHook'
import { RefModelParameterHook } from '../hooks/ModelParameterHooks'
import { selectCurrentDate } from '../../store/selectors/applicationSelectors'
import { selectTermInMonths } from '../../store/selectors/modelParametersSelector'
import { makeSelectScenarioGeneratorById } from '../../store/selectors/scenariosSlectors'



function CoverageProfileScenarios({chartParameters}) {

    
    // Model Parameters
    // -----------------
    const spot = RefSPOT()
    const termInMonths = useSelector(selectTermInMonths)
    const marketVolatility = RefMarketValueHook(con.MARKET_VOLATILITY)
    const confidenceLevel = RefModelParameterHook(con.ESC_CONFIDENCE_LEVEL)


    // Scenarios
    // -----------------
    const selectVarUpGenerator = useMemo(() => makeSelectScenarioGeneratorById(con.SCENARIO_VAR_UP),
        []
    )
    const selectVarDownGenerator = useMemo(() => makeSelectScenarioGeneratorById(con.SCENARIO_VAR_DOWN),
        []
    )

    const varUpGenerator = useSelector(selectVarUpGenerator)
    const varDownGenerator = useSelector(selectVarDownGenerator)

    // Application Parameters
    const historicPricesAndDates = RefMarketValueHook(con.HISTORIC_DOLLAR_INFO)
    const currentDate = useSelector(selectCurrentDate)

    // Multiline hook
    const [chartData, setChartData] = useState(() => [])
    const [objectReference, chart] = MultiLineChartHook({parameters : {[cf.TITLE] : "VALOR EN RIESGO DEL DOLAR", [cf.Y_LABEL] : "Tasa de Cambio", [cf.X_LABEL]: "Fecha", [cf.LEGEND_NUM_ROWS] : 2, ...chartParameters}})

    useEffect(() => {

        if(!chart.initialized)
            chart.initialize()
                
        const finalDate =  addMonths(currentDate, termInMonths)  
        
        const numPoints = daysBetweenDates(currentDate, finalDate)

        const dates = [...Array(numPoints).keys()]
            .map(i => addDays(currentDate, i).toJSDate())

        // Historic Values
        const historicLength = historicPricesAndDates[con.DATES].length
        const historicDates =
            historicPricesAndDates[con.DATES]
                .slice(historicLength - con.HISTORIC_SCENARIO_DAYS_TO_DISPLAY)
                .map(d => {
                    if (d instanceof Date) return d
                    if (d?.toJSDate) return d.toJSDate()
                    return new Date(d)
                })
                .concat([currentDate.toJSDate()])
        
        const historicPrices =
            historicPricesAndDates[con.TRM]
                .slice(historicLength - con.HISTORIC_SCENARIO_DAYS_TO_DISPLAY)
                .concat([spot])

        // Politic Var functions
        const politic_up = getGeneratorScenarioFunctionByVolatility(con.POLITIC_PERCENTAGE*marketVolatility, confidenceLevel, 1)   
        const politic_down = getGeneratorScenarioFunctionByVolatility(con.POLITIC_PERCENTAGE*marketVolatility, confidenceLevel, -1)
        
        const labelFun = (selObj,j) => {return(`${formatDateForUser(selObj.x[j])}: ${round(selObj.y[j])} COP`)}

        let data = [{[cf.NAME] : "Dolar Escenario Alcista Sin Politica",
                        [cf.X_VALUES] : dates,
                        [cf.Y_VALUES] : [...Array(numPoints).keys()].map((i) => varUpGenerator(spot,i)),
                        [cf.LABEL_GENERATING_FUNCTION] : labelFun},
                    {[cf.NAME] : "Dolar Escenario Bajista Sin Politica",
                        [cf.X_VALUES] : dates,
                        [cf.Y_VALUES] : [...Array(numPoints).keys()].map((i) => varDownGenerator(spot,i)),
                        [cf.LABEL_GENERATING_FUNCTION] : labelFun},
                    {[cf.NAME] : "TRM",
                        [cf.X_VALUES] : historicDates,
                        [cf.Y_VALUES] : historicPrices,
                        [cf.LABEL_GENERATING_FUNCTION] : labelFun},
                    {[cf.NAME] : "Dolar Escenario Alcista Con Politica",
                        [cf.X_VALUES] : dates,
                        [cf.Y_VALUES] : [...Array(numPoints).keys()].map((i) => politic_up(spot,i)),
                        [cf.LABEL_GENERATING_FUNCTION] : labelFun},
                    {[cf.NAME] : "Dolar Escenario Bajista Con Politica",
                        [cf.X_VALUES] : dates,
                        [cf.Y_VALUES] : [...Array(numPoints).keys()].map((i) => politic_down(spot,i)),
                        [cf.LABEL_GENERATING_FUNCTION] : labelFun}                  
                    
                    ]

        
            // Checks if structural change happened
            if(data.length !== chartData.length)
                chart.build(data)
            else
                chart.updateData(data)
    
            setChartData(data)
        

    }, [historicPricesAndDates, 
        currentDate,
        spot, 
        termInMonths,
        marketVolatility,
        confidenceLevel,
        varDownGenerator,
        varUpGenerator,
        chart,
        chartData.length])

    

    

    return (
        <div>
            <svg ref={objectReference}/>
        </div>
    )
}


CoverageProfileScenarios.defaultProps = {
    chartParameters : {}
  };

export default CoverageProfileScenarios
